import React, { useState } from "react";

//UI Material
import {
  Alert,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  SwipeableDrawer,
  TextField,
} from "@mui/material";

//Helpers
import {
  typographyForText,
  typographyForSubtitles,
  typographyForMainTitles,
} from "../../Helpers/typography";
import { fetchWithTokenJSONPost } from "../../Helpers/network";

//Material Icons
import { CheckBox, CheckBoxOutlineBlank, Upload } from "@mui/icons-material";

//Local components
import ContainedButton from "../basics/ContainedButton";
import ModalWindow from "../Modal";

export default function BillingDrawer({
  open,
  setOpen,
  order,
  getData,
  orderNumber,
}) {
  // ==========> Component States <==========
  //State for pre-tax price
  const [pretax, setPretax] = useState(order.billing.pretax);
  //State for aditional info
  const [aditional, setAditional] = useState(order.billing.aditional);
  //State for taxes
  const [includeTaxes, setIncludeTaxes] = useState(false);
  //State for loading
  const [isLoading, setIsLoading] = useState(false);
  //State for open modal
  const [modalOpen, setModalOpen] = useState(false);
  //State for modal text
  const [modalText, setModalText] = useState("");
  //State for modal description
  const [modalDescription, setModalDescription] = useState("");

  // ==========> Component Functions <==========
  //Function to return the total price amount
  const totalPrice = () => {
    if (includeTaxes) {
      const taxes = pretax * 0.13;
      return Number(pretax) + Number(taxes);
    } else {
      return pretax;
    }
  };

  //Function to set order billing information
  const setOrderBillingInformation = async () => {
    setIsLoading(true);
    try {
      const requestResponse = await fetchWithTokenJSONPost(
        "/orders/set-billing-info",
        {
          orderNumber,
          pretax,
          taxes: includeTaxes ? pretax * 0.13 : 0,
          amount: totalPrice(),
          aditional,
        }
      );
      const jsonResponse = await requestResponse.json();
      if (jsonResponse.status === "ok") {
        setModalText("Información de Facturación cambiada.");
        setModalDescription(
          `Se ha cambiado correctamente la información de facturación de esta orden de producción.`
        );
      } else {
        setModalText(
          "No se pudo cambiar la información de facturación de esta orden de producción."
        );
        setModalDescription(jsonResponse.description);
      }
    } catch (error) {
      setModalText("No se pudo realizar la acción.");
      setModalDescription(
        "Error al hacer los cambios, por favor verifica tu conexión a internet e inténtalo nuevamente."
      );
    }
    setModalOpen(true);
    setIsLoading(false);
    getData();
  };

  // ==========> Component <==========
  return (
    <SwipeableDrawer
      anchor="bottom"
      transitionDuration={600}
      disableSwipeToOpen={true}
      open={open}
      sx={{
        zIndex: "99",
        "& .MuiDrawer-paper": {
          justifyContent: "center",
          borderRadius: "35px 35px 0px 0px",
        },
      }}
      onClose={() => setOpen(false)}
      onOpen={() => {
        /**empty arrow function */
      }}
    >
      <Grid
        container
        justifyContent="center"
        sx={{ padding: "15px" }}
        textAlign="center"
        spacing={1}
      >
        <Grid item xs={12}>
          {typographyForSubtitles("Información de Facturación", "secondary")}
        </Grid>
        <Grid item xs={12} sm={10} sx={{ mb: 2 }}>
          <Alert severity="info">{`Información de facturación para la orden ${orderNumber}. Por favor asegúrese de completar los espacios con la información correcta.`}</Alert>
        </Grid>
        <Grid item xs={12} sm={5}>
          <Stack alignItems="center">
            {typographyForText("Monto sin impuestos (USD)", "bold")}
            <TextField
              type="number"
              value={pretax}
              onChange={(e) => {
                setPretax(e.target.value);
              }}
              sx={{ width: "100%" }}
            />
            <Stack direction="row" alignItems="center" justifyContent="center">
              <IconButton
                onClick={() => {
                  setIncludeTaxes(!includeTaxes);
                }}
              >
                {!includeTaxes ? <CheckBoxOutlineBlank /> : <CheckBox />}
              </IconButton>
              {typographyForText("Incluir IVA (13%)", "bold")}
            </Stack>
          </Stack>
        </Grid>

        <Grid item xs={12} sm={5}>
          <Stack alignItems="center">
            {typographyForText("Información adicional", "bold")}
            <TextField
              value={aditional}
              onChange={(e) => {
                setAditional(e.target.value);
              }}
              sx={{ width: "100%" }}
            />
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <Stack alignItems="center">
            {typographyForText("Monto Total")}
            {typographyForMainTitles(`$${Number(totalPrice()).toFixed(2)}`)}
          </Stack>
        </Grid>
        <Grid item xs={12} sx={{ mt: 4 }}>
          {isLoading ? (
            <CircularProgress />
          ) : (
            <Stack direction="row" justifyContent="center" spacing={1}>
              <ContainedButton
                text="Cancelar"
                color="error"
                onClick={() => setOpen(false)}
              />
              <ContainedButton
                text="Guardar cambios"
                endIcon={<Upload />}
                onClick={() => {
                  setOrderBillingInformation();
                }}
              />
            </Stack>
          )}
        </Grid>
      </Grid>
      <ModalWindow
        isOpen={modalOpen}
        handleClose={() => setModalOpen(false)}
        title={modalText}
        text={modalDescription}
        leftText="OK"
        leftClick={() => {
          setModalOpen(false);
          setOpen(false);
        }}
      />
    </SwipeableDrawer>
  );
}
